import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft, Loader2, ShieldAlert, CheckCircle2, Banknote, Layers } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import {
  adminListSettlements,
  adminReleaseSettlement,
  adminListSettlementsByEvent,
  adminReleaseEventSettlements,
} from "@/lib/settlements.functions";
import { formatCurrency } from "@/lib/money";

export const Route = createFileRoute("/admin/settlements")({
  head: () => ({ meta: [{ title: "Admin · Liquidaciones — ZYNTRA" }] }),
  component: AdminSettlements,
});

async function withAuth<T>(fn: (h: any) => Promise<T>): Promise<T> {
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  return fn({ authorization: token ? `Bearer ${token}` : "" });
}

interface SettlementRow {
  id: string;
  organizer_id: string;
  organizer_email: string | null;
  organizer_name: string | null;
  event_id: string;
  event_title: string | null;
  event_date: string | null;
  ticket_count: number;
  gross_amount: number;
  platform_fee_amount: number;
  organizer_net_amount: number;
  currency: string;
  status: string;
  created_at: string;
  released_at: string | null;
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; cls: string }> = {
    pending: { label: "Pendiente", cls: "border-warning/40 bg-warning/10 text-warning" },
    released: { label: "Liberada", cls: "border-primary/40 bg-primary/10 text-primary" },
    paid: { label: "Pagada", cls: "border-success/40 bg-success/10 text-success" },
    reversed: { label: "Revertida", cls: "border-destructive/40 bg-destructive/10 text-destructive" },
  };
  const m = map[status] || { label: status, cls: "border-border bg-secondary text-secondary-foreground" };
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${m.cls}`}>
      {m.label}
    </span>
  );
}

interface EventGroup {
  event_id: string;
  organizer_id: string;
  event_title: string | null;
  event_date: string | null;
  organizer_name: string | null;
  organizer_email: string | null;
  settlement_count: number;
  ticket_count: number;
  gross_amount: number;
  platform_fee_amount: number;
  organizer_net_amount: number;
  currency: string;
  oldest_created_at: string;
}

function AdminSettlements() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<"by_event" | "detailed">("by_event");
  const [filter, setFilter] = useState<string>("pending");
  const [rows, setRows] = useState<SettlementRow[]>([]);
  const [groups, setGroups] = useState<EventGroup[]>([]);
  const [confirmRow, setConfirmRow] = useState<SettlementRow | null>(null);
  const [confirmGroup, setConfirmGroup] = useState<EventGroup | null>(null);
  const [notes, setNotes] = useState("");
  const [releasing, setReleasing] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate({ to: "/login" }); return; }
    (async () => {
      const { data } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .eq("role", "admin")
        .maybeSingle();
      setIsAdmin(!!data);
      if (data) await load("pending");
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);

  async function load(status: string) {
    setFilter(status);
    try {
      if (view === "by_event") {
        const { groups } = await withAuth((h) =>
          adminListSettlementsByEvent({
            data: { status: status === "all" ? undefined : status },
            headers: h,
          } as any)
        );
        setGroups(groups as EventGroup[]);
      } else {
        const { settlements } = await withAuth((h) =>
          adminListSettlements({
            data: { status: status === "all" ? undefined : status },
            headers: h,
          } as any)
        );
        setRows(settlements as SettlementRow[]);
      }
    } catch (e) {
      console.error("[admin/settlements] load", e);
    }
  }

  useEffect(() => {
    if (isAdmin) load(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  async function handleRelease() {
    if (!confirmRow) return;
    setReleasing(true);
    try {
      await withAuth((h) =>
        adminReleaseSettlement({
          data: { settlementId: confirmRow.id, notes: notes || null },
          headers: h,
        } as any)
      );
      setConfirmRow(null);
      setNotes("");
      await load(filter);
    } catch (e) {
      console.error("[admin/settlements] release", e);
      alert((e as Error).message || "Error liberando liquidación");
    } finally {
      setReleasing(false);
    }
  }

  async function handleReleaseGroup() {
    if (!confirmGroup) return;
    setReleasing(true);
    try {
      const { releasedCount } = await withAuth((h) =>
        adminReleaseEventSettlements({
          data: {
            eventId: confirmGroup.event_id,
            organizerId: confirmGroup.organizer_id,
            notes: notes || null,
          },
          headers: h,
        } as any)
      );
      setConfirmGroup(null);
      setNotes("");
      await load(filter);
      alert(`Se liberaron ${releasedCount} liquidación(es).`);
    } catch (e) {
      console.error("[admin/settlements] release group", e);
      alert((e as Error).message || "Error liberando liquidaciones");
    } finally {
      setReleasing(false);
    }
  }

  if (authLoading || loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-md p-6 text-center">
        <ShieldAlert className="mx-auto h-10 w-10 text-destructive" />
        <h1 className="mt-3 text-xl font-bold text-foreground">Acceso restringido</h1>
        <p className="mt-1 text-sm text-muted-foreground">Solo administradores pueden ver esta página.</p>
        <Link to="/" className="mt-4 inline-block text-primary hover:underline">Volver</Link>
      </div>
    );
  }

  const totalPendingDetailed = rows
    .filter((r) => r.status === "pending")
    .reduce((s, r) => s + Number(r.organizer_net_amount || 0), 0);
  const totalPendingGrouped = groups.reduce((s, g) => s + Number(g.organizer_net_amount || 0), 0);
  const totalPending = view === "by_event" ? totalPendingGrouped : totalPendingDetailed;
  const totalCurrency = view === "by_event" ? (groups[0]?.currency || "CRC") : (rows[0]?.currency || "CRC");
  const showTotal = filter === "pending" && (view === "by_event" ? groups.length > 0 : rows.length > 0);

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6">
      <Link to="/dashboard/admin" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors">
        <ArrowLeft className="h-4 w-4" />
        Panel admin
      </Link>

      <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Liquidaciones a organizadores</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Cada compra primaria genera una liquidación pendiente. Liberala manualmente cuando corresponda.
          </p>
        </div>
        {showTotal ? (
          <div className="rounded-xl border border-warning/40 bg-warning/10 px-3 py-2 text-sm">
            <div className="text-xs text-muted-foreground">Total pendiente</div>
            <div className="font-semibold text-foreground tabular-nums">
              {formatCurrency(totalPending, totalCurrency)}
            </div>
          </div>
        ) : null}
      </div>

      {/* View toggle */}
      <div className="mb-3 inline-flex rounded-lg border border-border bg-card p-1">
        <button
          onClick={() => setView("by_event")}
          className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
            view === "by_event" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <Layers className="h-3.5 w-3.5" /> Por evento
        </button>
        <button
          onClick={() => setView("detailed")}
          className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
            view === "detailed" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
          }`}
        >
          Detallado
        </button>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        {[
          { v: "pending", label: "Pendientes" },
          { v: "released", label: "Liberadas" },
          { v: "paid", label: "Pagadas" },
          { v: "all", label: "Todas" },
        ].map((opt) => (
          <button
            key={opt.v}
            onClick={() => load(opt.v)}
            className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
              filter === opt.v
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border bg-card text-muted-foreground hover:text-foreground"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {view === "by_event" ? (
        groups.length === 0 ? (
          <div className="rounded-2xl border border-border bg-card p-10 text-center text-sm text-muted-foreground">
            Sin liquidaciones agrupadas en este filtro.
          </div>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-border bg-card">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-muted/30 text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 text-left">Evento</th>
                    <th className="px-4 py-3 text-left">Organizer</th>
                    <th className="px-4 py-3 text-right">Liq.</th>
                    <th className="px-4 py-3 text-right">Tickets</th>
                    <th className="px-4 py-3 text-right">Bruto</th>
                    <th className="px-4 py-3 text-right">Neto organizer</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {groups.map((g) => (
                    <tr key={`${g.event_id}-${g.organizer_id}`} className="border-b border-border/60 last:border-0">
                      <td className="px-4 py-3">
                        <div className="font-medium text-foreground">{g.event_title || "—"}</div>
                        <div className="text-xs text-muted-foreground">{g.event_date || ""}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-foreground">{g.organizer_name || "—"}</div>
                        <div className="text-xs text-muted-foreground">{g.organizer_email}</div>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">{g.settlement_count}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{g.ticket_count}</td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {formatCurrency(Number(g.gross_amount), g.currency)}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums font-semibold text-foreground">
                        {formatCurrency(Number(g.organizer_net_amount), g.currency)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {filter === "pending" ? (
                          <button
                            onClick={() => { setConfirmGroup(g); setNotes(""); }}
                            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground transition-opacity hover:opacity-90"
                          >
                            <Banknote className="h-3.5 w-3.5" />
                            Liberar todo
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-border bg-card p-10 text-center text-sm text-muted-foreground">
          Sin liquidaciones en este filtro.
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-border bg-card">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-muted/30 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 text-left">Evento</th>
                  <th className="px-4 py-3 text-left">Organizer</th>
                  <th className="px-4 py-3 text-right">Tickets</th>
                  <th className="px-4 py-3 text-right">Bruto</th>
                  <th className="px-4 py-3 text-right">Comisión</th>
                  <th className="px-4 py-3 text-right">Neto organizer</th>
                  <th className="px-4 py-3 text-left">Estado</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-border/60 last:border-0">
                    <td className="px-4 py-3">
                      <div className="font-medium text-foreground">{r.event_title || "—"}</div>
                      <div className="text-xs text-muted-foreground">
                        {r.event_date || ""} · {new Date(r.created_at).toLocaleDateString("es-CR")}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-foreground">{r.organizer_name || "—"}</div>
                      <div className="text-xs text-muted-foreground">{r.organizer_email}</div>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{r.ticket_count}</td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {formatCurrency(Number(r.gross_amount), r.currency)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-muted-foreground">
                      −{formatCurrency(Number(r.platform_fee_amount), r.currency)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums font-semibold text-foreground">
                      {formatCurrency(Number(r.organizer_net_amount), r.currency)}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={r.status} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      {r.status === "pending" ? (
                        <button
                          onClick={() => { setConfirmRow(r); setNotes(""); }}
                          className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground transition-opacity hover:opacity-90"
                        >
                          <Banknote className="h-3.5 w-3.5" />
                          Liberar
                        </button>
                      ) : r.status === "released" ? (
                        <span className="inline-flex items-center gap-1 text-xs text-success">
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          {r.released_at ? new Date(r.released_at).toLocaleDateString("es-CR") : ""}
                        </span>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Confirmation modal */}
      {confirmRow ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-2xl">
            <h2 className="text-lg font-bold text-foreground">Liberar liquidación</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Vas a acreditar{" "}
              <span className="font-semibold text-foreground">
                {formatCurrency(Number(confirmRow.organizer_net_amount), confirmRow.currency)}
              </span>{" "}
              al wallet de{" "}
              <span className="font-medium text-foreground">{confirmRow.organizer_name || confirmRow.organizer_email}</span>.
              Esta acción no se puede revertir desde la UI.
            </p>

            <div className="mt-4">
              <label className="text-xs font-medium text-muted-foreground">Notas internas (opcional)</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                maxLength={500}
                placeholder="Ej: Liberado tras evento confirmado sin disputas."
                className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none"
              />
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={() => { setConfirmRow(null); setNotes(""); }}
                disabled={releasing}
                className="rounded-lg border border-border bg-secondary px-4 py-2 text-sm font-medium text-secondary-foreground hover:bg-secondary/80 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleRelease}
                disabled={releasing}
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
              >
                {releasing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Banknote className="h-4 w-4" />}
                Confirmar liberación
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Bulk release modal */}
      {confirmGroup ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-2xl">
            <h2 className="text-lg font-bold text-foreground">Liberar todo el evento</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Vas a liberar{" "}
              <span className="font-semibold text-foreground">{confirmGroup.settlement_count}</span> liquidación(es)
              pendientes y acreditar{" "}
              <span className="font-semibold text-foreground">
                {formatCurrency(Number(confirmGroup.organizer_net_amount), confirmGroup.currency)}
              </span>{" "}
              al wallet de{" "}
              <span className="font-medium text-foreground">{confirmGroup.organizer_name || confirmGroup.organizer_email}</span>{" "}
              por el evento <span className="font-medium text-foreground">{confirmGroup.event_title}</span>.
              Esta acción no se puede revertir desde la UI.
            </p>

            <div className="mt-4">
              <label className="text-xs font-medium text-muted-foreground">Notas internas (opcional)</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                maxLength={500}
                placeholder="Ej: Evento finalizado sin disputas, liberación masiva."
                className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none"
              />
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={() => { setConfirmGroup(null); setNotes(""); }}
                disabled={releasing}
                className="rounded-lg border border-border bg-secondary px-4 py-2 text-sm font-medium text-secondary-foreground hover:bg-secondary/80 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleReleaseGroup}
                disabled={releasing}
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
              >
                {releasing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Banknote className="h-4 w-4" />}
                Liberar {confirmGroup.settlement_count} liq.
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
